import ExecAPI from 'appRoot/api/api';

// API client for the SQL Server Maintenance page.  Routes and request/response shapes are defined by
// Controllers/DbMaintenanceDtos.cs (controller `DbMaintenance`).  Every promise resolves with the JSON body even when the
// server reports a graceful failure, so callers must check `result.error` first.

/**
 * Gets the SQL Server Maintenance status: feature state (dry-run mode, secrets, endpoint configuration, orchestration),
 * the platform health verdict with per-web-server and per-database-server detail, the active run and run lock (if any),
 * and the automated maintenance schedule.
 * @param {Boolean} forceRefresh Bypass the few-seconds fleet status cache.  Pass true only for the manual Refresh button.
 */
export function GetDbMaintenanceStatus(forceRefresh)
{
	return ExecAPI("DbMaintenance/GetStatus", { forceRefresh: !!forceRefresh });
}
/**
 * Gets the read-only maintenance configuration summary: database server endpoints, default databases, cutover target,
 * window limits, schedule, and which secrets are configured (never the secret values themselves).
 */
export function GetDbMaintenanceConfig()
{
	return ExecAPI("DbMaintenance/GetConfig");
}
/**
 * Stores maintenance secrets in the encrypted maintenance secrets file.  For each field: null (or undefined) leaves the
 * stored value unchanged, "" clears it, any other string replaces it.  Values are never echoed back.  Audited.
 * @param {Object} secrets { redgateBackupPassword, backupEncryptionPassword, dbServerAgentSharedSecret }
 */
export function SetDbMaintenanceSecrets({ redgateBackupPassword, backupEncryptionPassword, dbServerAgentSharedSecret })
{
	return ExecAPI("DbMaintenance/SetSecrets", {
		redgateBackupPassword: nullIfUndefined(redgateBackupPassword),
		backupEncryptionPassword: nullIfUndefined(backupEncryptionPassword),
		dbServerAgentSharedSecret: nullIfUndefined(dbServerAgentSharedSecret)
	});
}
/**
 * Enumerates the user databases on one configured database server (DbMigrateJob.QuerySourceDatabases), with the
 * default selections flagged.
 * @param {String} sourceServerName Endpoint name (the primary or fallback server name).
 */
export function GetDbMaintenanceSourceDatabases(sourceServerName)
{
	return ExecAPI("DbMaintenance/GetSourceDatabases", { sourceServerName });
}
/**
 * Runs the read-only precondition checks (DbMigrate's PreconditionChecker plus the orchestrator's own checks) for the
 * given migration direction and databases.
 * @param {String} sourceServerName Migration source endpoint name.
 * @param {String} targetServerName Migration target endpoint name.
 * @param {String[]} dbNames Databases to migrate.
 */
export function RunDbMaintenancePreconditions(sourceServerName, targetServerName, dbNames)
{
	return ExecAPI("DbMaintenance/RunPreconditions", { sourceServerName, targetServerName, dbNames });
}
/**
 * Dry-run / preview: lists the exact steps, servers, files and target values a run would perform without executing
 * anything.  Also returns blockers that would prevent the run from starting.
 * @param {Object} request DbMaintenanceRunRequest: { kind, sourceServerName, targetServerName, dbNames, targetDbNames, cutoverTargetSeconds, readOnlyAllSourceDbs, confirmed }
 */
export function PreviewDbMaintenanceRun(request)
{
	return ExecAPI("DbMaintenance/Preview", request);
}
/**
 * Starts a maintenance run of the requested kind.  The administrator must have read the confirmation dialog
 * (request.confirmed = true).  Audited.
 * @param {Object} request DbMaintenanceRunRequest: { kind, sourceServerName, targetServerName, dbNames, targetDbNames, cutoverTargetSeconds, readOnlyAllSourceDbs, confirmed }
 */
export function StartDbMaintenanceRun(request)
{
	return ExecAPI("DbMaintenance/StartRun", request);
}
/**
 * Controls a run in progress.  Audited.
 * @param {String} runId Run to control.
 * @param {String} action One of Stop, ResumeAfterHold, EndWindowAfterHold, OverrideLock, Acknowledge.
 * @param {String} lockOwnerName For OverrideLock only: the current lock owner's display name, typed by the administrator.
 */
export function ControlDbMaintenanceRun(runId, action, lockOwnerName)
{
	return ExecAPI("DbMaintenance/ControlRun", { runId, action, lockOwnerName: nullIfUndefined(lockOwnerName) });
}
/**
 * Gets a snapshot of one run (live per-step progress while it runs, final state afterwards).
 * @param {String} runId Run to look up.
 */
export function GetDbMaintenanceRun(runId)
{
	return ExecAPI("DbMaintenance/GetRun", { runId });
}
/**
 * Gets the most recent runs (manual, scheduled and recovery) with their outcomes.
 * @param {Number} limit Maximum number of runs to return (server default 50).
 */
export function GetDbMaintenanceHistory(limit)
{
	let args = {};
	if (typeof limit === 'number' && limit > 0)
		args.limit = limit;
	return ExecAPI("DbMaintenance/GetHistory", args);
}
/**
 * Gets the HTML report for a finished run (DbMigrate's report plus the orchestrator's step summary).  Render it in a
 * sandboxed iframe.
 * @param {String} runId Run whose report to fetch.
 */
export function GetDbMaintenanceReport(runId)
{
	return ExecAPI("DbMaintenance/GetReport", { runId });
}

function nullIfUndefined(v)
{
	return typeof v === 'undefined' ? null : v;
}
